/**
 * Client-side redaction for the feedback diagnostics buffer (Card P244).
 *
 * ⛔⛔ WHY A SECOND COPY EXISTS, AND WHY IT IS NOT LAZINESS.
 *
 * The authority is routes/_feedback_shared.js redactForExport(), composed on
 * top of the four-times-hardened redactContacts(). `src/` CANNOT import from
 * `routes/`: create-react-app's ModuleScopePlugin forbids reaching outside
 * src/, and `src/` is not copied into the runtime image at all
 * (Dockerfile:56-64) — so a shared module would pass every local test and be
 * MODULE_NOT_FOUND in production. This repo already carries that scar
 * (database/rbac.js's note on src/lib/roleCatalog.js).
 *
 * ⚠ SO WHY REDACT ON THE CLIENT AT ALL, when the server redacts on export?
 * Because the two run at different MOMENTS and only one of them is early
 * enough to matter. The server's pass protects the Copy-for-Claude bundle.
 * This one protects the DATABASE: a console stack carrying a patient email in
 * a URL is scrubbed BEFORE it is transmitted, so it is never written down. A
 * redactor that runs only at export is one that stores the thing first.
 *
 * tests/unit/security/feedbackRedactParity.test.js asserts both copies remove
 * the same planted set, so a fix to one cannot silently skip the other.
 */

const EMAIL_RE = /(?<![A-Za-z0-9._%+-])[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const SEPARATED_DATE = /(?<![0-9])(\d{1,2})[/.\-](\d{1,2})[/.\-](\d{2,4})(?![0-9])/g;
const ISO_DATE_PLAIN = /(?<![0-9T:])(\d{4})-(\d{1,2})-(\d{1,2})(?![0-9T:])/g;

/*
 * ⚠ A RUN OF 7+ DIGITS, SEPARATORS INCLUDED. Phones, MRNs, account numbers and
 * long ids all take this shape. The threshold is deliberately BELOW a US phone
 * (10) so a partial or extension-bearing number still trips it; the cost is
 * that a 7-digit figure in prose is masked too, which in a bug report is a
 * trade worth making.
 */
const DIGIT_RUN = /(?<![0-9])(?:\d[ \-().]?){7,}\d(?![0-9])/g;

/*
 * ⛔ TIMESTAMPS ARE KEPT, DELIBERATELY. Every breadcrumb and API entry carries
 * one, and a diagnostics bundle whose clock has been redacted into bullets
 * cannot answer "what happened just before it broke" — the entire reason this
 * feature exists. They are parked behind a NUL placeholder before the digit
 * pass and restored afterwards.
 *
 * ⚠ NUL is stripped from the INPUT first, so the placeholder alphabet is
 * unreachable by the text being redacted — same reasoning and same ordering as
 * the server-side redactor this mirrors.
 */
const ISO_TIMESTAMP = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?/g;

export function redactText(text) {
  let s = String(text == null ? '' : text);
  if (!s) return s;

  s = s.replace(/\u0000/g, '');

  const kept = [];
  s = s.replace(ISO_TIMESTAMP, (m) => {
    kept.push(m);
    return `\u0000T${kept.length - 1}\u0000`;
  });

  s = s.replace(EMAIL_RE, (m) => `${m[0]}•••${m.slice(m.indexOf('@'))}`);
  s = s.replace(SEPARATED_DATE, '••/••/••••');
  s = s.replace(ISO_DATE_PLAIN, '••••-••-••');
  s = s.replace(DIGIT_RUN, (m) => {
    const digits = m.replace(/\D/g, '');
    return `••••${digits.slice(-4)}`;
  });

  s = s.replace(/\u0000T(\d+)\u0000/g, (_, i) => kept[Number(i)]);
  return s;
}

/**
 * ⚠⚠ A CLICKED CONTROL'S LABEL CAN ITSELF BE PHI, and the card's rule does not
 * reach it. "never typed field values" protects what the user ENTERED; it says
 * nothing about what the screen RENDERED. A contact-row button reading
 * "Open Jane Doe" is a patient name in the DOM, and copying it into a
 * breadcrumb writes PHI to a table designed not to hold any.
 *
 * So labels go through the same redactor as everything else, and are capped —
 * a breadcrumb is meant to say WHICH control, not to quote the screen.
 */
export const MAX_LABEL = 80;

export function redactLabel(text) {
  const s = redactText(String(text == null ? '' : text)).replace(/\s+/g, ' ').trim();
  return s.length > MAX_LABEL ? `${s.slice(0, MAX_LABEL - 1)}…` : s;
}
